import { useEffect, useState } from "react";
import { useSearchParams } from "next/navigation";
import { fetcher } from "@utils/fetchers";
import {
  getEventVenuePasswordStorageKey,
  getShowingsKey,
} from "../utils/showingsKey";

interface Props {
  eventId: number;
  eventVenueId: number;
}

type PasswordStatus = "loading" | "success" | "failure" | "complete" | null;

export const useEventVenuePasswordValidation = ({
  eventId,
  eventVenueId,
}: Props) => {
  const params = useSearchParams();
  const queryPassword = params.get("pw");
  const storageKey = getEventVenuePasswordStorageKey(eventVenueId);

  // `attempt` lets the same password be retried after a failure
  const [attempt, setAttempt] = useState<{ password: string; n: number }>({
    password: "",
    n: 0,
  });
  const [passwordStatus, setPasswordStatus] = useState<PasswordStatus>(null);

  const submitPassword = (password: string) =>
    setAttempt((prev) => ({ password, n: prev.n + 1 }));

  // Bypass with ?pw= takes priority over a previously validated password
  useEffect(() => {
    let stored: string | null = null;
    try {
      stored = sessionStorage.getItem(storageKey);
    } catch {}
    const initial = queryPassword || stored;
    if (initial) submitPassword(initial);
  }, [queryPassword, storageKey]);

  useEffect(() => {
    if (!attempt.password.trim()) return;
    let cancelled = false;

    const validate = async () => {
      setPasswordStatus("loading");
      let valid = false;
      try {
        await fetcher(
          getShowingsKey({
            eventId,
            eventVenueId,
            password: attempt.password,
          })
        );
        valid = true;
      } catch (err: any) {
        // A 404 means the password was accepted but there are no showings to show
        valid = err?.response?.status === 404;
      }
      if (cancelled) return;

      setPasswordStatus(valid ? "success" : "failure");
      try {
        if (valid) sessionStorage.setItem(storageKey, attempt.password);
        else sessionStorage.removeItem(storageKey);
      } catch {}
      setTimeout(
        () => !cancelled && setPasswordStatus(valid ? "complete" : null),
        1000
      );
    };

    validate();
    return () => {
      cancelled = true;
    };
  }, [attempt, eventId, eventVenueId, storageKey]);

  return {
    password: attempt.password,
    submitPassword,
    passwordStatus,
  };
};
